import { createServerFn } from "@tanstack/react-start";
import { getAuthContext, computeBalances, enrichFriendRequest } from "@/lib/services";
import {
  listFriends,
  listRequests,
  acceptRequest as dbAcceptRequest,
  rejectRequest as dbRejectRequest,
} from "@/db/friend";
import {
  listExpenses,
  getMonthlySpending,
  getSpendingByFriend,
  getDashboardSummary,
} from "@/db/expense";
import { listSettlements } from "@/db/settlement";
import type {
  FriendView,
  FriendRequestView,
  ExpenseView,
  SettlementView,
  BalanceView,
  MonthlySpendingPoint,
  FriendSpendingItem,
  DashboardSummary,
} from "@/lib/types";

export const loadDashboard = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId, email, name } = await getAuthContext();

  try {
    const [
      expenseRows,
      settlementRows,
      balanceData,
      friendRows,
      requestData,
      monthlyRows,
      friendSpendingRows,
      summaryRows,
    ] = await Promise.all([
      listExpenses(db, userId, null, 5, null),
      listSettlements(db, userId, null, 5, null),
      computeBalances(db, userId),
      listFriends(db, userId),
      listRequests(db, userId),
      getMonthlySpending(db, userId),
      getSpendingByFriend(db, userId),
      getDashboardSummary(db, userId),
    ]);

    const expenses: ExpenseView[] = expenseRows.map((e) => ({
      id: e.id.toString(),
      description: e.description,
      amount: e.amount,
      currency: e.currency,
      paidBy: e.paid_by,
      splitType: e.split_type,
      splits: [],
      date: new Date(e.date).toISOString().split("T")[0],
    }));

    const settlements: SettlementView[] = settlementRows.map((s) => ({
      id: s.id.toString(),
      fromUserId: s.from_user_id,
      toUserId: s.to_user_id,
      amount: s.amount,
      currency: s.currency,
      date: new Date(s.date).toISOString().split("T")[0],
    }));

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    const incoming: FriendRequestView[] = await Promise.all(
      requestData.incoming.map((r) => enrichFriendRequest(db, r)),
    );

    // Collect currencies first, then build a full 6-month skeleton
    const spendingCurrencies = new Set<string>();
    for (const row of monthlyRows) {
      spendingCurrencies.add(row.currency);
    }

    // Generate all 6 months (current month back to 5 months ago)
    const now = new Date();
    const monthKeys: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      monthKeys.push(key);
    }

    // Build points for every month, defaulting currencies to 0
    const monthMap = new Map<string, MonthlySpendingPoint>();
    for (const key of monthKeys) {
      const [y, m] = key.split("-");
      const date = new Date(Date.UTC(Number(y), Number(m) - 1));
      const label = date.toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      });
      const point: MonthlySpendingPoint = { month: key, label };
      for (const c of spendingCurrencies) {
        point[c] = 0;
      }
      monthMap.set(key, point);
    }

    // Fill in actual data
    for (const row of monthlyRows) {
      const point = monthMap.get(row.month);
      if (point) {
        point[row.currency] = parseFloat(row.total);
      }
    }
    const monthlySpending = Array.from(monthMap.values());

    // Friend spending: top 5, resolve names
    const friendMap = new Map<string, { id: string; email: string; name: string }>();
    for (const f of friendRows) {
      friendMap.set(f.user_id, { id: f.user_id, email: f.email, name: f.name });
    }
    const friendSpending: FriendSpendingItem[] = friendSpendingRows.slice(0, 5).map((row) => {
      const friend = friendMap.get(row.friend_user_id);
      return {
        name: friend?.name || friend?.email || row.friend_user_id,
        value: parseFloat(row.total),
        currency: row.currency,
      };
    });

    // Dashboard summary
    const summary: DashboardSummary[] = summaryRows.map((row) => ({
      currency: row.currency,
      expenseCount: parseInt(row.expense_count, 10),
      expenseTotal: row.expense_total,
      settlementCount: parseInt(row.settlement_count, 10),
      settlementTotal: row.settlement_total,
    }));

    return {
      expenses,
      settlements,
      balances: balanceData.balances,
      totals: balanceData.totals,
      friends,
      incoming,
      currentUser: { id: userId, email, name },
      monthlySpending,
      spendingCurrencies: Array.from(spendingCurrencies),
      friendSpending,
      summary,
    };
  } catch (error) {
    console.error("Failed to load dashboard:", error);
    return {
      expenses: [] as ExpenseView[],
      settlements: [] as SettlementView[],
      balances: [] as BalanceView[],
      totals: [] as BalanceView[],
      friends: [] as FriendView[],
      incoming: [] as FriendRequestView[],
      currentUser: { id: userId, email, name },
      monthlySpending: [] as MonthlySpendingPoint[],
      spendingCurrencies: [] as string[],
      friendSpending: [] as FriendSpendingItem[],
      summary: [] as DashboardSummary[],
    };
  }
});

export const acceptDashboardRequest = createServerFn({ method: "POST" })
  .inputValidator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbAcceptRequest(db, Number(data.requestId), userId);
  });

export const rejectDashboardRequest = createServerFn({ method: "POST" })
  .inputValidator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbRejectRequest(db, Number(data.requestId), userId);
  });
